import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";
import { track } from "@/lib/analytics";
import { useMotionTier } from "@/lib/motionTier";
import { cn } from "@/lib/utils";
import { userHasActed, autoSoundAllowed, markUserMuted, claimSpeaker, releaseSpeaker, waitForGesture, stopWaiting } from "@/lib/autoSound";

/**
 * RealScreen: what goes inside a PhoneStage. A real recording of the app (a clip, looping; muted unless it has a sound pill) or a real screenshot of it, with a label that says which.
 *
 *   video   { src, poster, alt }  autoplays only while it is on screen (the file is not even requested before it is near the viewport)
 *   image   { src, alt }          a still screenshot of the real app screen
 *   aspect  CSS aspect-ratio of the screen, e.g. "4 / 5" (a sport clip) or "9 / 16" (the film)
 *   label   the honest caption, e.g. "Real session, filmed on a phone"
 *   sound   the film carries the app's real coach voice: sound is ON BY DEFAULT where the browser allows it (see lib/autoSound.js: once the person has tapped or
 *           clicked anywhere, a film that scrolls into view speaks by itself; before that it plays muted and the first tap, click or key press anywhere turns it on),
 *           and a pill shows the state ("Tap for sound" / "Mute"). Turning sound on restarts the film from 0 so the voice starts with its first line.
 *           Also on when video.sound is true. autoSound={false} keeps one film muted until its own pill is tapped (the back phone of a pair).
 *           soundClass moves the pill when a film has its own controls at the bottom (default "bottom-3").
 *           It mutes by itself when scrolled off screen, and only one film has sound at a time. No pill if the file has no audio track.
 *
 * Reduced motion, data saver and low-end phones (motion tier "static") get the poster and a play button instead of autoplay; a browser that
 * refuses to autoplay (low-power mode) gets the same. The videos are static files under public/, never part of the JS bundle.
 */
export default function RealScreen({ video, image, aspect = "4 / 5", label, labelClass = "top-2", className, fit = "cover", sound = false, autoSound = true, soundClass = "bottom-3", active = true, ...rest }) {
  const tier = useMotionTier();
  const still = tier === "static";
  const box = useRef(null);
  const vid = useRef(null);
  const [near, setNear] = useState(false); // close enough to start fetching
  const [blocked, setBlocked] = useState(false); // autoplay refused: show the play button
  const [playing, setPlaying] = useState(false);
  const [loud, setLoud] = useState(false); // sound is on
  const [silent, setSilent] = useState(false); // the file has no audio track: no pill
  const [woken, setWoken] = useState(active !== false); // ctive={false} keeps a layer that is not in view yet from fetching or playing (the home story's stacked screens)
  const activeRef = useRef(active !== false);
  activeRef.current = active !== false;
  const [seen, setSeen] = useState(false); // mostly on screen
  const wantSound = (sound || !!video?.sound) && !silent;
  const file = video ? String(video.src || "").split("/").pop() : "";
  const id = useRef(`rs${Math.random().toString(36).slice(2)}`).current;

  // back to muted (scrolled away, another film took over, or the person tapped Mute)
  const mute = useCallback(() => {
    const v = vid.current;
    if (v) v.muted = true;
    setLoud(false);
    releaseSpeaker(mute);
  }, []);
  useEffect(() => () => { releaseSpeaker(mute); stopWaiting(id); }, [mute, id]);

  useEffect(() => {
    const el = box.current;
    if (!video || !el || typeof IntersectionObserver !== "function") { if (video) setNear(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setNear(true);
      setSeen(e.isIntersecting && e.intersectionRatio >= 0.4);
      const v = vid.current;
      if (v && !e.isIntersecting) { v.pause?.(); mute(); } // never any sound from a film that is off screen
      if (!v || still || !activeRef.current) return;
      if (e.isIntersecting) v.play?.().then(() => setBlocked(false)).catch(() => setBlocked(true));
      else v.pause?.();
    }, { rootMargin: "200px 0px", threshold: [0.2, 0.4] });
    io.observe(el);
    return () => io.disconnect();
  }, [video, still, mute]);

  // the source arrives with `near`: start it as soon as it can
  useEffect(() => {
    const v = vid.current;
    if (!v || !near || still || !woken || active === false) return;
    v.play?.().then(() => setBlocked(false)).catch(() => setBlocked(true));
  }, [near, still, woken, active]);

  // a layer that is not the one in view: wake it when it becomes the one, pause and silence it when it stops being
  useEffect(() => {
    if (active !== false) { setWoken(true); return; }
    const v = vid.current;
    if (v) v.pause?.();
    mute();
  }, [active, mute]);

  // no audio track (where the browser can tell): no sound pill. Chrome cannot tell, so it keeps the pill.
  const onMeta = (e) => {
    const v = e.currentTarget;
    if ((v.audioTracks && v.audioTracks.length === 0) || v.mozHasAudio === false) setSilent(true);
  };

  // the tap is the user gesture the browser wants: unmute, restart so the voice starts with its first line, and play, all inside the handler
  const turnSoundOn = (by = "tap") => {
    const v = vid.current; if (!v) return;
    claimSpeaker(mute);
    setNear(true);
    v.muted = false;
    try { v.currentTime = 0; } catch { /* not seekable yet: it starts at 0 anyway */ }
    const ok = () => { claimSpeaker(mute); setLoud(true); setBlocked(false); track("real_screen_sound", { src: file, by }); };
    const refused = () => { // sound refused: back to the muted loop, the pill comes back, and a later gesture can try again
      v.muted = true; setLoud(false); releaseSpeaker(mute);
      v.play?.().catch(() => {});
      track("real_screen_sound_blocked", { src: file, by });
      if (by !== "tap" && autoSound) waitForGesture(id, (b) => speakRef.current(b));
    };
    const p = v.play?.();
    if (p && p.then) p.then(ok).catch(refused); else ok();
  };
  const speakRef = useRef(turnSoundOn);
  speakRef.current = turnSoundOn;
  const toggleSound = () => {
    if (loud) { markUserMuted(); stopWaiting(id); mute(); track("real_screen_mute", { src: file, by: "tap" }); } else turnSoundOn("tap");
  };

  // sound on by default: speak by itself when mostly on screen, once the browser allows it (the person has already tapped something), else at the first tap
  useEffect(() => {
    if (!wantSound || !autoSound || still || !near || !woken || active === false || !seen || loud || !autoSoundAllowed()) { stopWaiting(id); return undefined; }
    // already tapped something: speak now. Not yet: still TRY with sound (Chrome allows it for visitors it has seen watch videos here, and after a click that brought them to this page);
    // if the browser refuses, turnSoundOn falls back to the muted loop and waits for the first tap, click or key press
    speakRef.current(userHasActed() ? "auto" : "try");
    return () => stopWaiting(id);
  }, [wantSound, autoSound, still, near, woken, active, seen, loud, id]);

  const playByHand = () => { const v = vid.current; if (!v) return; setNear(true); v.play?.().then(() => setBlocked(false)).catch(() => {}); };

  return (
    <div ref={box} className={cn("relative w-full overflow-hidden bg-zinc-950", className)} style={{ aspectRatio: aspect }} data-testid="real-screen" {...rest}>
      {video ? (
        <>
          <video
            ref={vid} className={cn("absolute inset-0 w-full h-full", fit === "cover" ? "object-cover" : "object-contain")}
            poster={video.poster} muted loop playsInline autoPlay={!still} preload={near && woken ? "metadata" : "none"} aria-label={video.alt}
            src={near && woken ? video.src : undefined} data-testid="real-screen-video"
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onLoadedMetadata={onMeta}
          />
          {(still || blocked) && !playing && (
            <button type="button" onClick={playByHand} aria-label="Play the recording" className="absolute inset-0 flex items-center justify-center bg-black/10" data-testid="real-screen-play">
              <span className="w-14 h-14 rounded-full bg-lime-400 text-zinc-950 flex items-center justify-center shadow-glow"><Play className="w-6 h-6 ml-0.5 lg:w-7 lg:h-7" fill="currentColor" /></span>
            </button>
          )}
          {wantSound && near && woken && active !== false && (
            <button type="button" onClick={toggleSound} aria-label={loud ? "Mute the sound" : "Turn the sound on"} data-testid="real-screen-sound"
              style={{ transform: "translate3d(-50%, 0, 20px)" }} // the 20px of depth is on purpose: inside the tilted phone, Chrome sends taps to the video's own layer unless the button sits in front of it
              className={cn("absolute left-1/2 z-10 inline-flex items-center gap-1.5 min-h-11 rounded-full bg-zinc-950/85 border px-4 text-[13px] lg:text-[15px] font-bold text-white whitespace-nowrap shadow-float backdrop-blur-md", !loud && autoSound ? "border-lime-400/70 animate-pulse" : "border-white/20", soundClass)}>
              {loud ? <Volume2 className="h-4 w-4 lg:w-5 lg:h-5" aria-hidden="true" /> : <VolumeX className="h-4 w-4 lg:w-5 lg:h-5" aria-hidden="true" />}
              {loud ? "Mute" : "Tap for sound"}
            </button>
          )}
        </>
      ) : image ? (
        <img src={image.src} alt={image.alt} loading="lazy" decoding="async" className={cn("absolute inset-0 w-full h-full", fit === "cover" ? "object-cover object-top" : "object-contain")} data-testid="real-screen-image" />
      ) : null}
      {label && (
        <span className={`absolute right-2.5 ${labelClass} max-w-[calc(100%-1.25rem)] text-[10px] lg:text-xs uppercase tracking-wider font-semibold text-zinc-200 bg-zinc-950/80 border border-white/10 rounded-full px-2 py-0.5`} data-testid="real-screen-label">{label}</span>
      )}
    </div>
  );
}
